import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {PrivateTalk} from '@app/modules/wrtc/models/private-talk';
import produce from 'immer';
import {ViewportTab} from '@app/modules/shared/data/constants';
import {Message, MessageType} from '@app/models/message';
import {CurrentClass} from '@app/models/current-class';
import {SVGClipboard} from '@app/modules/svg/model/SVGClipboard';

export interface ClassroomStateModel {
  roomNum: number;
  vcrURL: string;
  vcrDiagnosticsURL: string;
  cmsURL: string;
  studentPortalUrl: string;
  newStudentPortalUrl: string;
  isClassroomMuted: boolean;
  isConnectedToSocket: boolean;
  isConnectedToWRTCRoom: boolean;
  privateTalkInfo: PrivateTalk;
  currentClass: CurrentClass;
  isIpadMode: boolean;
  isInFullScreenMode: boolean;
  stickers: string[];
  isDnDEnabled: boolean;
  isTimerEnabled: boolean;
  isVideoSectionOpen: boolean;
  selectedBoardTab: ViewportTab;
  selectedPrivateBoardTab: ViewportTab;
  showPrivateWhiteboard: boolean;
  messages: Message [];
  classWideMassage: Message [];
  isChatHidden: boolean;
  isChatCollapsed: boolean;
  isStudentsSectionCollapsed: boolean;
  isLocalTracksFetched: boolean;
  atLeastOneObserverInClassroom: boolean;
  atLeastOneSupportInClassroom: boolean;
  showAllStudentsAreOnWB: boolean;
  isRoomMutedBecauseOfCurrentUserBlockedMicAndCam: boolean;
}

export namespace ClassroomAction {
  export class SetClassroom {
    static readonly type = '[Classroom] set group id';

    constructor(public roomNum: number) {}
  }

  export class SetVCRApiURL {
    static readonly type = '[Classroom] set api url';

    constructor(public url: string) {}
  }

  export class SetClassroomMuteState {
    static readonly type = '[Classroom] set mute state';

    constructor(public isClassroomMuted: boolean) {
    }
  }

  export class SetServiceUrls {
    static readonly type = '[Classroom] set service urls';

    constructor(public cmsUrl: string, public studentPortalUrl: string, public newStudentPortalUrl: string) {}
  }

  export class ToggleConnectedToSocket {
    static readonly type = '[Classroom] set connected to socket';

    constructor(public isConnected: boolean) {
    }
  }

  export class ToggleConnectedToWRTCRoom {
    static readonly type = '[Classroom] set connected to WRTC room';

    constructor(public isConnected: boolean) {
    }
  }

  export class ToggleVideoSectionOpen {
    static readonly type = '[Classroom] toggle video section open state';

    constructor(public isOpen: boolean) {
    }
  }

  export class SetCurrentClassInfo {
    static readonly type = '[Classroom] set current class info';

    constructor(public currentClassInfo: CurrentClass) {
    }
  }

  export class SetIPadMode {
    static readonly type = '[Classroom] set IPad mode';

    constructor(public isIPadMode: boolean) {
    }
  }

  export class ToggleShowAllStudentsAreOnWB {
    static readonly type = '[Room] toggle show all student are on wb message.';

    constructor(public show: boolean) {
    }
  }

  export class ToggleRoomMutedBecauseOfBlockedMicAnCam {
    static readonly type = '[Room] toggle room mute state (cased by blocked mic and web cam).';

    constructor(public isMuted: boolean) {
    }
  }


  export class SetStickers {
    static readonly type = '[Classroom] set classroom stickers';

    constructor(public stickers: string []) {
    }
  }

  export class ToggleDnDMode {
    static readonly type = '[Classroom] toggle DnD mode';

    constructor(public enable: boolean) {
    }
  }

  export class ToggleTimer {
    static readonly type = '[Classroom] toggle timer mode';

    constructor(public enable: boolean) {
    }
  }

  export class ToggleFullScreenMode {
    static readonly type = '[Classroom] toggle fullscreen mode';

    constructor(public enable: boolean) {
    }
  }

  export class AddPrivateTalkParticipant {
    static readonly type = '[Classroom-private-talk] add private chat participant';

    constructor(public userId: number, public wasMuted: boolean) {
    }
  }

  export class RemovePrivateTalkParticipant {
    static readonly type = '[Classroom-private-talk] remove private chat participant';

    constructor(public id: number) {
    }
  }

  export class EndPrivateTalk {
    static readonly type = '[Classroom-private-talk] end private chat';

    constructor() {
    }
  }

  export class StartPrivateWhiteboard {
    static readonly type = '[Classroom-private-whiteboard] start private whiteboard';

    constructor(public show: boolean = false) {
    }
  }

  export class EndPrivateWhiteboard {
    static readonly type = '[Classroom-private-whiteboard] end private whiteboard';

    constructor() {
    }
  }

  export class ShowPrivateWhiteboard {
    static readonly type = '[Classroom-private-whiteboard] show private whiteboard';

    constructor(public show: boolean) {
    }
  }

  export class SetBoardTab {
    static readonly type = '[Classroom] set board tab index';

    constructor(public tabIndex: ViewportTab) {
    }
  }

  export class SetPrivateBoardTab {
    static readonly type = '[Classroom] set private board tab index';

    constructor(public tabIndex: ViewportTab) {
    }
  }

  export class ToggleIsChatHidden {
    static readonly type = '[Classroom] toggle is chat hidden';

    constructor(public isChatHidden: boolean) {
    }
  }

  export class ToggleChatCollapse {
    static readonly type = '[Classroom] toggle chat expand/collapse';

    constructor(public isChatCollapsed: boolean) {
    }
  }

  export class AddMessage {
    static readonly type = '[Classroom] add message';

    constructor(public messageModel: Message) {
    }
  }

  export class AddClassWideMessage {
    static readonly type = '[Classroom] add class wide message';

    constructor(public messageModel: Message) {
    }
  }

  export class ClearNonSystemMessages {
    static readonly type = '[Classroom] delete non system messages';

    constructor() {
    }
  }

  export class ClearAllMessages {
    static readonly type = '[Classroom] delete all messages';

    constructor() {
    }
  }

  export class ClearUserMessages {
    static readonly type = '[Classroom] remove user messages';

    constructor(public userId: number, public deleteSystemMessages: boolean) {
    }
  }

  export class RemoveMessageByTargetIdAndText {
    static readonly type = '[Classroom] remove message by target Id and text';

    constructor(public targetId: number, public text: string) {
    }
  }

  export class ToggleStudentsSectionCollapsed {
    static readonly type = '[Classroom] toggle student section  expand/collapse';

    constructor(public isCollapsed: boolean) {
    }
  }

  export class ToggleLocalTracksFetched {
    static readonly type = '[Classroom] toggle local tracks fetched ';

    constructor(public isFetched: boolean) {
    }
  }

  export class SetAtLeastOneObserverInClassroom {
    static readonly type = '[Classroom] toggle is at least one observer present in classroom.';

    constructor(public isPresent: boolean) {
    }
  }

  export class SetAtLeastOneSupportInClassroom {
    static readonly type = '[Classroom] toggle is at least one support present in classroom.';

    constructor(public isPresent: boolean) {
    }
  }
}

@State<ClassroomStateModel>({
  name: ClassroomState.storeName,
  defaults: ClassroomState.defaults
})
@Injectable()
export class ClassroomState {
  static readonly storeName = 'room';
  static readonly defaults: ClassroomStateModel = {
    roomNum: 0,
    vcrURL: '',
    vcrDiagnosticsURL: '/diagnostics',
    cmsURL: '',
    studentPortalUrl: '',
    newStudentPortalUrl: '',
    isClassroomMuted: false,
    isConnectedToSocket: false,
    isConnectedToWRTCRoom: false,
    privateTalkInfo: new PrivateTalk(false, {}, false),
    currentClass: null,
    isIpadMode: false,
    isInFullScreenMode: false,
    isDnDEnabled: false,
    isTimerEnabled: false,
    isVideoSectionOpen: true,
    selectedBoardTab: ViewportTab.WHITEBOARD,
    selectedPrivateBoardTab: ViewportTab.WHITEBOARD,
    showPrivateWhiteboard: false,
    messages: [],
    classWideMassage:[],
    isChatHidden: true,
    isChatCollapsed: true,
    isStudentsSectionCollapsed: false,
    isLocalTracksFetched: false,
    atLeastOneObserverInClassroom: false,
    atLeastOneSupportInClassroom: false,
    showAllStudentsAreOnWB: false,
    stickers: [
      'candy',
      'cupcake',
      'lightening',
      'puppy',
      'race-car0',
      'race-car1',
      'rocket',
      'smile',
      'snowman',
      'tulip',
      'winking',
      'apple',
      'cookie',
      'heart',
      'ice-cream',
      'snowflake',
      'star',
      'thumb-up',
      'cake',
    ],
    isRoomMutedBecauseOfCurrentUserBlockedMicAndCam: false,
  };

  @Selector()
  static getClassroom(state: ClassroomStateModel) {
    return state;
  }

  @Action(ClassroomAction.SetClassroom)
  setClassroom(ctx: StateContext<ClassroomStateModel>, {roomNum}: ClassroomAction.SetClassroom) {
    ctx.patchState({roomNum});
  }
  @Action(ClassroomAction.SetVCRApiURL)
  setApiURL(ctx: StateContext<ClassroomStateModel>, {url}: ClassroomAction.SetVCRApiURL) {
    ctx.patchState({vcrURL: url});
  }

  @Action(ClassroomAction.SetClassroomMuteState)
  setClassroomMuteState(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.SetClassroomMuteState) {
    ctx.patchState({
      isClassroomMuted: info.isClassroomMuted,
    });
  }

  @Action(ClassroomAction.SetServiceUrls)
  setServiceUrls(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.SetServiceUrls) {
    ctx.patchState({
      cmsURL: info.cmsUrl,
      studentPortalUrl: info.studentPortalUrl,
      newStudentPortalUrl: info.newStudentPortalUrl,
    });
  }

  @Action(ClassroomAction.ToggleConnectedToSocket)
  toggleConnectedToSocket(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleConnectedToSocket) {
    ctx.patchState({
      isConnectedToSocket: info.isConnected,
    });
  }

  @Action(ClassroomAction.ToggleConnectedToWRTCRoom)
  toggleConnectedToWRTCRoom(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleConnectedToWRTCRoom) {
    ctx.patchState({
      isConnectedToWRTCRoom: info.isConnected,
    });
  }

  @Action(ClassroomAction.ToggleVideoSectionOpen)
  toggleVideoSectionOpen(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleVideoSectionOpen) {
    ctx.patchState({
      isVideoSectionOpen: info.isOpen,
    });
  }

  @Action(ClassroomAction.SetCurrentClassInfo)
  setCurrentClassInfo(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.SetCurrentClassInfo) {
    ctx.patchState({
      currentClass: info.currentClassInfo,
    });
  }

  @Action(ClassroomAction.SetIPadMode)
  setIPadMode(ctx: StateContext<ClassroomStateModel>, {isIPadMode}: ClassroomAction.SetIPadMode) {
    ctx.patchState({
      isIpadMode: isIPadMode,
    });
  }

  @Action(ClassroomAction.SetStickers)
  setStickers(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.SetStickers) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      stickers: info.stickers,
    });
  }


  @Action(ClassroomAction.ToggleDnDMode)
  toggleDnDMode(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleDnDMode) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      isDnDEnabled: info.enable,
    });
  }

  @Action(ClassroomAction.ToggleTimer)
  toggleTimerMode(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleTimer) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      isTimerEnabled: info.enable,
    });
  }

  @Action(ClassroomAction.ToggleFullScreenMode)
  toggleFullScreenMode(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleFullScreenMode) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      isInFullScreenMode: info.enable
    });
  }

  @Action(ClassroomAction.AddPrivateTalkParticipant)
  addPrivateTalkParticipant(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.AddPrivateTalkParticipant) {
    ctx.setState(produce((state) => {
      state.privateTalkInfo = {
        isStarted: true,
        participants: {
          ...state.privateTalkInfo.participants,
        },
        isPrivateWhiteboardOn: state.privateTalkInfo.isPrivateWhiteboardOn
      };
      state.privateTalkInfo.participants[info.userId] = {wasMuted: info.wasMuted};
    }));
  }

  @Action(ClassroomAction.RemovePrivateTalkParticipant)
  removePrivateTalkParticipant(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.RemovePrivateTalkParticipant) {
    ctx.setState(produce((state) => {
      delete state.privateTalkInfo.participants[info.id];
    }));
  }

  @Action(ClassroomAction.StartPrivateWhiteboard)
  startPrivateWhiteboard(ctx: StateContext<ClassroomStateModel>, {show}: ClassroomAction.StartPrivateWhiteboard) {
    ctx.patchState({
      privateTalkInfo: {
        participants: ctx.getState().privateTalkInfo.participants,
        isStarted: ctx.getState().privateTalkInfo.isStarted,
        isPrivateWhiteboardOn: true
      },
      showPrivateWhiteboard: show
    });
  }

  @Action(ClassroomAction.EndPrivateWhiteboard)
  endPrivateWhiteboard(ctx: StateContext<ClassroomStateModel>) {
    ctx.patchState({
      privateTalkInfo: {
        participants: ctx.getState().privateTalkInfo.participants,
        isStarted: ctx.getState().privateTalkInfo.isStarted,
        isPrivateWhiteboardOn: false
      },
      showPrivateWhiteboard: false
    });
  }

  @Action(ClassroomAction.ShowPrivateWhiteboard)
  showPrivateWhiteboard(ctx: StateContext<ClassroomStateModel>, {show}: ClassroomAction.ShowPrivateWhiteboard) {
    ctx.patchState({
      showPrivateWhiteboard: show
    });
  }

  @Action(ClassroomAction.EndPrivateTalk)
  endPrivateTalk(ctx: StateContext<ClassroomStateModel>) {
    ctx.patchState({privateTalkInfo: {participants: [], isStarted: false, isPrivateWhiteboardOn: false}, showPrivateWhiteboard: false});
  }

  @Action(ClassroomAction.SetBoardTab)
  setBoardTab(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.SetBoardTab) {
    ctx.patchState({selectedBoardTab: info.tabIndex});
  }

  @Action(ClassroomAction.SetPrivateBoardTab)
  setPrivateBoardTab(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.SetPrivateBoardTab) {
    ctx.patchState({selectedPrivateBoardTab: info.tabIndex});
  }

  @Action(ClassroomAction.ToggleIsChatHidden)
  toggleIsChatHidden(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleIsChatHidden) {
    ctx.patchState({isChatHidden: info.isChatHidden});
  }

  @Action(ClassroomAction.ToggleChatCollapse)
  toggleChatCollapse(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleChatCollapse) {
    ctx.patchState({isChatCollapsed: info.isChatCollapsed});
  }

  @Action(ClassroomAction.AddMessage)
  addMessage(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.AddMessage) {
    const state = ctx.getState();
    const messagesModels = Object.assign([], state.messages);
    messagesModels.push(info.messageModel);

    ctx.patchState({messages: messagesModels});
  }

  @Action(ClassroomAction.AddClassWideMessage)
  addClassWideMessage(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.AddClassWideMessage) {
    const state = ctx.getState();
    const messagesModels = Object.assign([], state.classWideMassage);
    messagesModels.push(info.messageModel);

    ctx.patchState({classWideMassage: messagesModels});
  }

  @Action(ClassroomAction.ClearNonSystemMessages)
  clearNonSystemMessages(ctx: StateContext<ClassroomStateModel>) {
    const systemMessages: Message[] = ctx.getState().messages.filter((message: Message) => message.messageType === MessageType.SYSTEM);
    ctx.patchState({messages: systemMessages});
  }

  @Action(ClassroomAction.ClearAllMessages)
  removeAllMessages(ctx: StateContext<ClassroomStateModel>) {
    ctx.patchState({messages: []});
  }

  @Action(ClassroomAction.ClearUserMessages)
  removeUserMessages(ctx: StateContext<ClassroomStateModel>, {userId, deleteSystemMessages}: ClassroomAction.ClearUserMessages) {
    const state = ctx.getState();

    /* remove all messages which are not:
    * 1. sent to student and target user is message target
    * 2. sent to staff and target user is message target or message sender
    * 3. system message, target user is message target and system messages should be deleted */
    const storedMessages: Message[] = state.messages.filter((message: Message) =>
      !(message.messageType === MessageType.TO_STUDENT && message.targetId === userId ||
      message.messageType === MessageType.TO_STAFF && (message.senderId === userId || message.targetId === userId) ||
      deleteSystemMessages && message.messageType === MessageType.SYSTEM && message.targetId === userId)
    );
    ctx.patchState({messages: storedMessages});
  }

  @Action(ClassroomAction.RemoveMessageByTargetIdAndText)
  removeMessageByTargetIdAndText(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.RemoveMessageByTargetIdAndText) {
    const state = ctx.getState();
    let messagesModels = Object.assign([], state.messages);
    messagesModels = messagesModels.filter(value => value.targetId !== info.targetId || value.text !== info.text);

    ctx.patchState({messages: messagesModels});
  }

  @Action(ClassroomAction.ToggleStudentsSectionCollapsed)
  toggleStudentsSectionCollapsed(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleStudentsSectionCollapsed) {
    ctx.patchState({
      isStudentsSectionCollapsed: info.isCollapsed,
    });
  }

  @Action(ClassroomAction.ToggleLocalTracksFetched)
  toggleLocalTracksFetched(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleLocalTracksFetched) {
    ctx.patchState({
      isLocalTracksFetched: info.isFetched,
    });
  }

  @Action(ClassroomAction.SetAtLeastOneObserverInClassroom)
  toggleAtLeastOneObserverInClassroom(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.SetAtLeastOneObserverInClassroom) {
    ctx.patchState({
      atLeastOneObserverInClassroom: info.isPresent,
    });
  }

  @Action(ClassroomAction.SetAtLeastOneSupportInClassroom)
  toggleAtLeastOneSupportInClassroom(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.SetAtLeastOneSupportInClassroom) {
    ctx.patchState({
      atLeastOneSupportInClassroom: info.isPresent,
    });
  }

  @Action(ClassroomAction.ToggleShowAllStudentsAreOnWB)
  toggleShowAllStudentsAreOnWB(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleShowAllStudentsAreOnWB) {
    const state = ctx.getState();

    ctx.patchState({
      ...state,
      showAllStudentsAreOnWB: info.show,
    });
  }

  @Action(ClassroomAction.ToggleRoomMutedBecauseOfBlockedMicAnCam)
  toggleRoomMutedBecauseOfBlockedMicAnCam(ctx: StateContext<ClassroomStateModel>, info: ClassroomAction.ToggleRoomMutedBecauseOfBlockedMicAnCam) {
    const state = ctx.getState();
    ctx.patchState({
      ...state,
      isRoomMutedBecauseOfCurrentUserBlockedMicAndCam: info.isMuted
    });
  }

}
